import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import { feedbackAPI } from '../../services/api';

const RATING_EMOJIS: Record<number, string> = { 1: '😡', 2: '😞', 3: '😐', 4: '😊', 5: '🤩' };
const RATING_LABELS: Record<number, string> = { 1: 'Terrible', 2: 'Bad', 3: 'Okay', 4: 'Good', 5: 'Excellent' };
const STATUS_COLORS: Record<string, string> = {
  new: 'primary', reviewed: 'info', resolved: 'success', spam: 'danger',
};

const FeedbackPage: React.FC = () => {
  const { currentAsset } = useAuth();
  const assetId = currentAsset?.id;
  const isIntegrated = !!(currentAsset?.is_verified);

  const [loading, setLoading] = useState(true);
  const [feedbacks, setFeedbacks] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [widgetConfig, setWidgetConfig] = useState<any>(null);
  const [pagination, setPagination] = useState({ page: 1, total: 0, pages: 1 });
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const [updating, setUpdating] = useState<number | null>(null);
  const [togglingWidget, setTogglingWidget] = useState(false);
  const [exportingCsv, setExportingCsv] = useState(false);

  const [filters, setFilters] = useState({
    status: '', rating: '', date_from: '', date_to: '', search: '', page: 1,
  });

  const fetchData = async () => {
    if (!assetId) return;
    setLoading(true);
    try {
      const [fbRes, statsRes, cfgRes] = await Promise.all([
        feedbackAPI.list({ asset_id: assetId, ...filters }),
        feedbackAPI.getStats(assetId),
        feedbackAPI.getConfig(assetId),
      ]);
      setFeedbacks(fbRes.data.data.feedbacks || []);
      setPagination(fbRes.data.data.pagination || { page: 1, total: 0, pages: 1 });
      setStats(statsRes.data.data);
      setWidgetConfig(cfgRes.data.data);
    } catch {
      toast.error('Failed to load feedbacks');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [assetId, filters]);

  const handleToggleWidget = async () => {
    if (!widgetConfig || togglingWidget) return;
    setTogglingWidget(true);
    const newEnabled = widgetConfig.is_enabled ? 0 : 1;
    try {
      await feedbackAPI.updateConfig({ ...widgetConfig, asset_id: assetId, is_enabled: newEnabled });
      setWidgetConfig((prev: any) => ({ ...prev, is_enabled: newEnabled }));
      toast.success(newEnabled ? 'Feedback widget enabled' : 'Feedback widget disabled');
    } catch {
      toast.error('Failed to update widget status');
    } finally {
      setTogglingWidget(false);
    }
  };

  const handleStatusChange = async (id: number, status: string) => {
    setUpdating(id);
    try {
      await feedbackAPI.updateStatus(id, status);
      toast.success('Status updated');
      fetchData();
    } catch {
      toast.error('Failed to update status');
    } finally {
      setUpdating(null);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this feedback?')) return;
    try {
      await feedbackAPI.deleteFeedback(id);
      toast.success('Deleted');
      fetchData();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const setFilter = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handleExportCsv = async () => {
    if (!assetId || exportingCsv) return;
    setExportingCsv(true);
    try {
      const res = await feedbackAPI.export({ asset_id: assetId, ...filters });
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `feedbacks-${assetId}-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to export feedbacks');
    } finally {
      setExportingCsv(false);
    }
  };

  const handleExportPdf = () => {
    if (!feedbacks.length) { toast.error('No feedbacks to export'); return; }
    const rows = feedbacks.map(fb => `
      <tr>
        <td>${new Date(fb.created_at).toLocaleDateString()}</td>
        <td>${RATING_EMOJIS[fb.rating] || ''} ${RATING_LABELS[fb.rating] || fb.rating}</td>
        <td>${(fb.comment || '').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</td>
        <td>${fb.name || ''}</td>
        <td>${fb.email || ''}</td>
        <td>${fb.status}</td>
      </tr>`).join('');
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Feedbacks</title>
      <style>body{font-family:Arial,sans-serif;font-size:12px}table{width:100%;border-collapse:collapse}
      th,td{border:1px solid #ddd;padding:6px 8px;text-align:left}th{background:#f3f4f6;font-weight:600}
      h2{margin-bottom:8px}p{margin-bottom:16px;color:#666}</style></head>
      <body><h2>Customer Feedbacks</h2><p>Exported on ${new Date().toLocaleDateString()}</p>
      <table><thead><tr><th>Date</th><th>Rating</th><th>Comment</th><th>Name</th><th>Email</th><th>Status</th></tr></thead>
      <tbody>${rows}</tbody></table></body></html>`;
    const w = window.open('', '_blank');
    if (w) { w.document.write(html); w.document.close(); w.print(); }
  };

  const widgetEnabled = widgetConfig?.is_enabled;
  const widgetType = widgetConfig?.widget_type || 'button';
  const totalCount = stats?.total || 0;
  const avgRating = stats?.avg_rating;
  const dist = stats?.rating_distribution || {};
  const maxDist = Math.max(...Object.values(dist).map(Number), 1);

  return (
    <DashboardLayout>
      <div className="container-fluid py-4">

        {/* Header */}
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div>
            <h4 className="fw-bold mb-0">
              <i className="bi bi-chat-square-text text-primary me-2"></i>Customer Feedbacks
            </h4>
            <p className="text-muted small mb-0">Collect and manage feedback submitted via your website widget.</p>
          </div>
          <div className="d-flex gap-2">
            <button
              className="btn btn-sm btn-outline-success"
              onClick={handleExportCsv}
              disabled={exportingCsv || loading}
              title="Download CSV (Excel compatible)"
            >
              {exportingCsv
                ? <span className="spinner-border spinner-border-sm me-1"></span>
                : <i className="bi bi-file-earmark-spreadsheet me-1"></i>}
              Excel
            </button>
            <button
              className="btn btn-sm btn-outline-danger"
              onClick={handleExportPdf}
              disabled={loading || feedbacks.length === 0}
              title="Print / Export PDF"
            >
              <i className="bi bi-file-earmark-pdf me-1"></i>PDF
            </button>
          </div>
        </div>

        {/* Status banner */}
        <div className="row g-3 mb-4">
          {/* Widget status card */}
          <div className="col-md-6">
            <div className={`card border-0 shadow-sm h-100 border-start border-4 ${widgetEnabled ? 'border-success' : 'border-secondary'}`}>
              <div className="card-body d-flex align-items-center justify-content-between gap-3">
                <div>
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <span className={`badge ${widgetEnabled ? 'bg-success' : 'bg-secondary'} px-2`}>
                      {widgetEnabled ? '● Active' : '○ Disabled'}
                    </span>
                    <span className="text-muted small">
                      {widgetEnabled
                        ? `Showing ${widgetType === 'popup' ? 'popup' : widgetType === 'both' ? 'popup + button' : 'floating button'} on your website`
                        : 'Widget is not showing on your website'}
                    </span>
                  </div>
                  <div className="fw-semibold">Feedback Widget</div>
                  <div className="text-muted small mt-1">
                    <Link to="/integration/feedback" className="text-primary text-decoration-none">
                      <i className="bi bi-sliders me-1"></i>Configure widget settings
                    </Link>
                  </div>
                </div>
                <button
                  className={`btn btn-sm ${widgetEnabled ? 'btn-outline-danger' : 'btn-success'} flex-shrink-0`}
                  onClick={handleToggleWidget}
                  disabled={togglingWidget}
                >
                  {togglingWidget
                    ? <span className="spinner-border spinner-border-sm"></span>
                    : widgetEnabled ? 'Disable' : 'Enable Widget'}
                </button>
              </div>
            </div>
          </div>

          {/* Integration status card */}
          <div className="col-md-6">
            <div className={`card border-0 shadow-sm h-100 border-start border-4 ${isIntegrated ? 'border-info' : 'border-warning'}`}>
              <div className="card-body">
                <div className="d-flex align-items-center gap-2 mb-1">
                  <span className={`badge ${isIntegrated ? 'bg-info' : 'bg-warning text-dark'} px-2`}>
                    {isIntegrated ? '✓ Integrated' : '⚠ Not Verified'}
                  </span>
                </div>
                <div className="fw-semibold">Tracking Script</div>
                {isIntegrated ? (
                  <p className="text-muted small mb-0 mt-1">
                    <i className="bi bi-check-circle-fill text-success me-1"></i>
                    Your website has the CloudAyn script — the widget will appear automatically when enabled.
                  </p>
                ) : (
                  <p className="text-muted small mb-0 mt-1">
                    <i className="bi bi-exclamation-triangle-fill text-warning me-1"></i>
                    Script not detected on your website. The feedback widget
                    is embedded via <code>cloudayn.js</code> — it won't show until the script is added.{' '}
                    <Link to="/integration" className="text-primary text-decoration-none">Add script →</Link>
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="row g-3 mb-4">
          <div className="col-md-3">
            <div className="card border-0 shadow-sm">
              <div className="card-body text-center py-3">
                <div className="fs-3 fw-bold text-primary">{totalCount}</div>
                <div className="text-muted small">Total Submissions</div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card border-0 shadow-sm">
              <div className="card-body text-center py-3">
                <div className="fs-3 fw-bold text-warning">
                  {avgRating
                    ? <span>{RATING_EMOJIS[Math.round(Number(avgRating))]} {avgRating}</span>
                    : '—'}
                </div>
                <div className="text-muted small">Average Rating</div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card border-0 shadow-sm">
              <div className="card-body text-center py-3">
                <div className="fs-3 fw-bold text-danger">{stats?.spam_count || 0}</div>
                <div className="text-muted small">Spam Detected</div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card border-0 shadow-sm">
              <div className="card-body py-3">
                <div className="text-muted small mb-2 fw-semibold">Rating Distribution</div>
                {[5, 4, 3, 2, 1].map(r => (
                  <div key={r} className="d-flex align-items-center gap-2 mb-1">
                    <span style={{ width: 20, fontSize: 13 }}>{RATING_EMOJIS[r]}</span>
                    <div className="flex-grow-1 bg-light rounded" style={{ height: 8 }}>
                      <div className="rounded" style={{
                        height: 8,
                        width: `${((dist[r] || 0) / maxDist) * 100}%`,
                        background: r >= 4 ? '#22c55e' : r === 3 ? '#f59e0b' : '#ef4444',
                        transition: 'width .3s',
                      }} />
                    </div>
                    <span className="text-muted" style={{ fontSize: 12, width: 24, textAlign: 'right' }}>{dist[r] || 0}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="card border-0 shadow-sm mb-3">
          <div className="card-body py-2">
            <div className="row g-2 align-items-center">
              <div className="col-md-3">
                <input
                  className="form-control form-control-sm"
                  placeholder="Search email, name, comment..."
                  value={filters.search}
                  onChange={e => setFilter('search', e.target.value)}
                />
              </div>
              <div className="col-md-2">
                <select className="form-select form-select-sm" value={filters.status} onChange={e => setFilter('status', e.target.value)}>
                  <option value="">All Status</option>
                  <option value="new">New</option>
                  <option value="reviewed">Reviewed</option>
                  <option value="resolved">Resolved</option>
                  <option value="spam">Spam</option>
                </select>
              </div>
              <div className="col-md-2">
                <select className="form-select form-select-sm" value={filters.rating} onChange={e => setFilter('rating', e.target.value)}>
                  <option value="">All Ratings</option>
                  {[5, 4, 3, 2, 1].map(r => (
                    <option key={r} value={r}>{RATING_EMOJIS[r]} {RATING_LABELS[r]}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-2">
                <input type="date" className="form-control form-control-sm" value={filters.date_from} onChange={e => setFilter('date_from', e.target.value)} />
              </div>
              <div className="col-md-2">
                <input type="date" className="form-control form-control-sm" value={filters.date_to} onChange={e => setFilter('date_to', e.target.value)} />
              </div>
              <div className="col-md-1">
                <button className="btn btn-outline-secondary btn-sm w-100" onClick={() => setFilters({ status: '', rating: '', date_from: '', date_to: '', search: '', page: 1 })}>
                  Clear
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="card border-0 shadow-sm">
          <div className="card-body p-0">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status"></div>
              </div>
            ) : feedbacks.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <i className="bi bi-chat-square-text fs-1 d-block mb-2 opacity-25"></i>
                {!widgetEnabled
                  ? <span>Enable the widget above to start collecting feedback from your visitors.</span>
                  : <span>No feedbacks yet. Submissions will appear here when visitors use the widget.</span>}
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: 40 }}></th>
                      <th>Date</th>
                      <th>Rating</th>
                      <th>Comment</th>
                      <th>Name / Email</th>
                      <th>Page</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {feedbacks.map(fb => (
                      <React.Fragment key={fb.id}>
                        <tr>
                          <td>
                            <button
                              className="btn btn-sm btn-outline-secondary py-0 px-1"
                              onClick={() => setExpandedRow(expandedRow === fb.id ? null : fb.id)}
                            >
                              <i className={`bi bi-chevron-${expandedRow === fb.id ? 'up' : 'down'}`}></i>
                            </button>
                          </td>
                          <td>
                            <div className="small">{new Date(fb.created_at).toLocaleDateString()}</div>
                            <div className="text-muted" style={{ fontSize: 11 }}>{new Date(fb.created_at).toLocaleTimeString()}</div>
                          </td>
                          <td>
                            <span title={RATING_LABELS[fb.rating]} style={{ fontSize: 20 }}>{RATING_EMOJIS[fb.rating] || fb.rating}</span>
                          </td>
                          <td style={{ maxWidth: 200 }}>
                            <div className="text-truncate small" title={fb.comment}>
                              {fb.comment || <span className="text-muted fst-italic">No comment</span>}
                            </div>
                          </td>
                          <td>
                            <div className="small fw-semibold">{fb.name || '—'}</div>
                            <div className="text-muted small">{fb.email || '—'}</div>
                          </td>
                          <td style={{ maxWidth: 150 }}>
                            {fb.page_url ? (
                              <a href={fb.page_url} target="_blank" rel="noopener noreferrer" className="small text-truncate d-block" style={{ maxWidth: 140 }} title={fb.page_url}>
                                {fb.page_url.replace(/^https?:\/\//, '').substring(0, 30)}
                              </a>
                            ) : <span className="text-muted small">—</span>}
                          </td>
                          <td>
                            <span className={`badge bg-${STATUS_COLORS[fb.status] || 'secondary'}`}>{fb.status}</span>
                          </td>
                          <td>
                            <div className="d-flex gap-1">
                              <select
                                className="form-select form-select-sm"
                                style={{ width: 120 }}
                                value={fb.status}
                                disabled={updating === fb.id}
                                onChange={e => handleStatusChange(fb.id, e.target.value)}
                              >
                                <option value="new">New</option>
                                <option value="reviewed">Reviewed</option>
                                <option value="resolved">Resolved</option>
                                <option value="spam">Spam</option>
                              </select>
                              <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(fb.id)}>
                                <i className="bi bi-trash"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                        {expandedRow === fb.id && (
                          <tr className="table-light">
                            <td colSpan={8}>
                              <div className="p-3">
                                <div className="row g-3">
                                  <div className="col-md-6">
                                    <div className="small fw-semibold text-muted mb-1">Full Comment</div>
                                    <div className="small">{fb.comment || <span className="fst-italic text-muted">No comment</span>}</div>
                                  </div>
                                  {fb.extra_answers && Object.keys(fb.extra_answers).length > 0 && (
                                    <div className="col-md-6">
                                      <div className="small fw-semibold text-muted mb-1">Extra Answers</div>
                                      {Object.entries(fb.extra_answers).map(([k, v]: any) => (
                                        <div key={k} className="small"><strong>{k}:</strong> {v}</div>
                                      ))}
                                    </div>
                                  )}
                                  <div className="col-md-6">
                                    <div className="small fw-semibold text-muted mb-1">Details</div>
                                    <div className="small">Phone: {fb.phone || '—'}</div>
                                    <div className="small">IP: {fb.ip_address || '—'}</div>
                                    <div className="small">Interaction Score: {fb.interaction_score ?? '—'}</div>
                                    <div className="small">Spam Score: {fb.spam_score ?? '—'}</div>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          {pagination.pages > 1 && (
            <div className="card-footer border-0 bg-transparent d-flex justify-content-between align-items-center">
              <div className="text-muted small">
                Page {pagination.page} of {pagination.pages} ({pagination.total} total)
              </div>
              <div className="d-flex gap-1">
                <button className="btn btn-sm btn-outline-secondary" disabled={filters.page <= 1} onClick={() => setFilters(p => ({ ...p, page: p.page - 1 }))}>
                  <i className="bi bi-chevron-left"></i>
                </button>
                <button className="btn btn-sm btn-outline-secondary" disabled={filters.page >= pagination.pages} onClick={() => setFilters(p => ({ ...p, page: p.page + 1 }))}>
                  <i className="bi bi-chevron-right"></i>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default FeedbackPage;
